import { Component, inject, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SearchProfileService } from '@services/search-profile.service';
import { CommonService } from '@services/common.service';
import { SearchHistory, SearchHistoryResponse } from '@models/SearchHistory';
import { CommonModule } from '@angular/common';
import { AppConstants } from '@constants/app.constant';
import { DisplayColNamePipe } from 'src/app/pipes/display-col-name';
import { ConvertUtcPipe } from 'src/app/pipes/convert-utc';
import { Subscription } from 'rxjs';

/**
 * Component for displaying the user's search history in a sortable, paginated table.
 * Allows users to review and re-execute previous searches.
 */
@Component({
  selector: 'app-search-history',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    DisplayColNamePipe,
    ConvertUtcPipe
  ],
  templateUrl: './search-history.component.html',
  styleUrl: './search-history.component.scss',
})
export class SearchHistoryComponent implements OnInit, OnDestroy {
  readonly constants = AppConstants;
  private readonly spService = inject(SearchProfileService);
  private readonly commonService = inject(CommonService);

  listSearchHistory: SearchHistory[] = [];
  totalItems = 0;
  pageSize = 20;
  page = 0;
  displayedColumns: string[] = [
    'action',
    'search_date',
    'rcm_id',
    'source',
    'source_id',
    'name',
    'address',
    'city',
    'state',
    'zip',
    'dea_num',
    'hin_no',
    'npi',
    'id_340b',
    'sln',
    'tddd_oh',
    'type',
    'hcce'
  ];
  dataSource = new MatTableDataSource<SearchHistory>(this.listSearchHistory);

  @ViewChild('sortHi') sortHi!: MatSort;
  @ViewChild('historyPaginator') paginator!: MatPaginator;

  private subscriptions = new Subscription();
  errorMsg = '';

  ngOnInit(): void {
    this.getSearchHistory();
  }

  /**
   * Fetches the user's search history and updates the table.
   */
  getSearchHistory(): void {
    this.commonService.setShowLoader(true);
    const sub = this.spService.getUserSearchHistory().subscribe({
      next: (res: SearchHistoryResponse) => {
        this.listSearchHistory = res.records;
        this.dataSource.data = this.listSearchHistory;
        this.totalItems = this.listSearchHistory.length;
        this.setupSortGP();
        this.setupPaginator();
        this.commonService.setShowLoader(false);
        this.errorMsg = '';
      },
      error: () => {
        this.commonService.setShowLoader(false);
        this.errorMsg = 'Failed to load search history.';
      }
    });
    this.subscriptions.add(sub);
  }

  /**
   * Handler for re-executing a search from history.
   */
  onSearch(row: SearchHistory): void {
    // Implement your search logic here
    console.log(`Search clicked for ${row.name}`);
  }

  /**
   * Sets up the paginator for the table.
   */
  setupPaginator(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.pageIndex = this.page;
      this.paginator.pageSize = this.pageSize;
      this.paginator.length = this.totalItems;
    }
  }

  /**
   * Handles page change events for the table.
   */
  onPageChangeCR(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.page = event.pageIndex;
  }

  /**
   * Navigates back to the previous screen.
   */
  goBack(): void {
    this.commonService.bsBackButton.next('');
  }

  /**
   * Sets up the default sorting for the table.
   */
  setupSortGP(): void {
    if (this.sortHi) {
      this.sortHi.active = 'search_date';
      this.sortHi.direction = 'desc';
      this.dataSource.sort = this.sortHi;
      this.sortHi.sortChange.emit({
        active: this.sortHi.active,
        direction: this.sortHi.direction,
      });
    }
  }

  performSearch(data: SearchHistory): void {
    data.dea=data.dea_num;
    data.tddd=data.tddd_oh;
    this.commonService.searchFlag.set(false);
    this.commonService.navFrom = 'History';
    this.commonService.spSubject.next(data);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
